/**
 * Frontend Configuration — ported from FE/config.js
 *
 * Auto-detects localhost vs production and sets URLs accordingly.
 * Values that differ per environment live in .env files; everything
 * else is derived here.
 */

const isLocal = ['localhost', '127.0.0.1'].includes(window.location.hostname);
const narrativeMinChars = Number(import.meta.env.VITE_TR_NARRATIVE_MIN_CHARS?.trim() || 15);

export const CONFIG = {
  ENV: isLocal ? 'development' : 'production',

  // Backend API — env var takes priority; fallback auto-selects prod vs dev by hostname
  get BACKEND_BASE_URL(): string {
    if (import.meta.env.VITE_BACKEND_URL) return import.meta.env.VITE_BACKEND_URL;
    const devHosts = ['junior-dev.justizialaw.co.uk', 'd2x7anl33f4b5y.cloudfront.net'];
    return devHosts.includes(window.location.hostname)
      ? 'https://backend-dev.justizialaw.co.uk'
      : 'https://backend-app.justizialaw.co.uk';
  },

  // AWS Cognito
  COGNITO_DOMAIN:
    import.meta.env.VITE_COGNITO_DOMAIN ?? 'eu-west-1tbhd42c47.auth.eu-west-1.amazoncognito.com',
  COGNITO_CLIENT_ID: import.meta.env.VITE_COGNITO_CLIENT_ID ?? '27luduneechitv7oc01nd6et4m',

  // Redirect URI — dynamically use the current origin so it works with any hosting URL.
  // The origin MUST be registered in Cognito's "Allowed callback URLs".
  get COGNITO_REDIRECT_URI(): string {
    return import.meta.env.VITE_COGNITO_REDIRECT_URI ?? `${window.location.origin}/home.html`;
  },

  // Computed
  get COGNITO_LOGIN_URL(): string {
    return `https://${this.COGNITO_DOMAIN}/login?client_id=${this.COGNITO_CLIENT_ID}&redirect_uri=${encodeURIComponent(this.COGNITO_REDIRECT_URI)}&response_type=code&scope=email+openid+phone`;
  },
  get COGNITO_LOGOUT_URL(): string {
    // logout_uri must exactly match a registered "Allowed sign-out URL" in Cognito.
    return `https://${this.COGNITO_DOMAIN}/logout?client_id=${this.COGNITO_CLIENT_ID}&logout_uri=${encodeURIComponent(window.location.origin + '/home.html')}`;
  },

  // DynamoDB
  DYNAMODB_REGION: 'eu-west-1',
  DYNAMODB_DOCUMENT_LEDGER_TABLE: 'DocumentLedger',

  // API Gateway
  API_GATEWAY_AIRCALL: 'https://0k8fm2fs13.execute-api.eu-west-1.amazonaws.com',

  // Superset
  SUPERSET_DOMAIN: 'https://superset.justizialaw.co.uk',

  // Aircall user-ID mapping (keyed by lowercase, no-space username)
  AIRCALL_USER_MAP: {
    filipharemza: 1396791,
    philipsparrow: 1396796,
    melodyhelm: 1402477,
    anmolsingla: 1423724,
    christhomas: 1431383,
    sudharsansivaraman: 1492218,
    alishakhan: 1770017,
  } as Record<string, number>,

  // TR Billing v2 feature flags (frontend)
  TR_BILLING_V2_FORM_ENABLED: true,
  TR_NARRATIVE_MIN_CHARS:
    Number.isSafeInteger(narrativeMinChars) && narrativeMinChars >= 0 ? narrativeMinChars : 15,
} as const;

// ============================================================================
// DOCUMENT UPLOAD CONFIGURATION
// ============================================================================

/**
 * Maximum number of simultaneous uploads when users drag & drop multiple files.
 * Prevents overwhelming the browser/network while maintaining good throughput.
 */
export const CONCURRENT_UPLOAD_LIMIT = 6;
